import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Ring } from "@uiball/loaders";
import { Copy, AlertTriangle, Edit, Trash2 } from "lucide-react";
import ReactDOM from "react-dom";
import { useHistory } from "react-router-dom";
import { createProductAuction, getFee, getExternalStoreFee, forceStopAuction, deleteProductAuction, editProductAuction, deleteMyProduct, updateProduct } from "../../../../utils/services/user.service";
import { useChainContext } from "../../../../containers/context/context";
import DataTable from "../../../../shared/components/DataTable";
import StatusBadge from "../../auction/components/StatusBadge";
import { CustomSelect } from "../../../../shared/components/CustomSelect";
import { Section } from "./shared";
import countriesData from "../../createstore/country.json";
import { ActionWindowCard, ActionWindowLabel, DateRow, DateChip, DateDot, CountdownChip } from "./traceability/TraceabilityStyles";
import {
  ModalOverlay,
  ModalContainer,
  ModalTitle,
  ModalMessage,
  ModalActions,
  ModalButton,
} from "./AuctionStyles";
import {
  AuctionBox,
  Led,
  AuctionSidebar,
  ProductEditSidebar,
  PaymentMethodContainer,
  PaymentMethodOption,
  PaymentMethodLeft,
  PaymentMethodLabel,
  PaymentMethodName,
  PaymentMethodSubtext,
  PaymentMethodRadio,
  ComingSoonBadge,
  DetailModal,
  DetailModalContent,
  CloseButton as StyledCloseButton,
  DetailSection,
  DetailLabel,
  DetailValue,
  TierBox,
  TierGridContainer,
  TierCard,
  UnlockChip,
  PulseDot,
  DateTimeInput,
  FormGroup,
  Label,
  Input,
  TierInputGroup,
  AddTierButton,
  TierTable,
  TotalPriceContainer,
  TotalPriceHeader,
  TotalPriceInfo,
  TotalPriceLabel,
  TotalPriceValue,
  BreakdownSection,
  BreakdownRow,
  BreakdownLabel,
  BreakdownValue,
  TotalRow,
  TotalLabel,
  TotalValue,
  CalculationBox,
  CalculationText,
  SelectWrapper,
  UserIdCell,
  UserAvatar,
  UserAvatarPlaceholder,
  UserInfo,
  UserName,
  UserEmail,
  GallerySection,
  GalleryGrid,
  MainStage,
  MainStageInner,
  MainImage,
  StageTopRow,
  StageChip,
  ChipDot,
  StageActions,
  IconBtn,
  ThumbsRail,
  ThumbBtn,
  ThumbImg,
  LightboxOverlay,
  LightboxFrame,
  LbTop,
  LbTitle,
  LbControls,
  LbStage,
  LbImage,
  LbArrow,
  LbThumbs,
  PurchaseDescCard,
  PurchaseDescLabel,
  PurchaseDescText,
} from "./AuctionStyles";
import PaymentSidebar from "@/shared/components/PaymentSidebar";

/* ==========================
   GALLERY PRO COMPONENT
========================== */
function GalleryPRO({ images }) {
  const safeImages = Array.isArray(images) && images.length ? images : [];
  const [index, setIndex] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [touchStartX, setTouchStartX] = useState(null);

  const activeImg = safeImages[index] || "";

  const openLb = (i) => {
    if (!safeImages.length) return;
    setIndex(Math.max(0, Math.min(safeImages.length - 1, i)));
    setZoom(1);
    setLightbox(true);
  };

  const closeLb = () => {
    setLightbox(false);
    setZoom(1);
  };

  const next = () => {
    if (!safeImages.length) return;
    setIndex((i) => (i + 1) % safeImages.length);
    setZoom(1);
  };

  const prev = () => {
    if (!safeImages.length) return;
    setIndex((i) => (i - 1 + safeImages.length) % safeImages.length);
    setZoom(1);
  };

  const zoomIn = () => setZoom((z) => Math.min(3, Math.max(1, z + 0.2)));
  const zoomOut = () => setZoom((z) => Math.min(3, Math.max(1, z - 0.2)));
  const resetZoom = () => setZoom(1);

  useEffect(() => {
    if (!lightbox) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") closeLb();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "+" || e.key === "=") zoomIn();
      if (e.key === "-") zoomOut();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightbox, safeImages.length]);

  const handleWheel = (e) => {
    e.preventDefault();
    const direction = e.deltaY > 0 ? -0.12 : 0.12;
    setZoom((z) => Math.min(3, Math.max(1, z + direction)));
  };

  const handleTouchStart = (e) => {
    if (e.touches.length === 1) setTouchStartX(e.touches[0].clientX);
  };

  const handleTouchEnd = (e) => {
    if (touchStartX == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX;
    if (dx > 40) prev();
    if (dx < -40) next();
    setTouchStartX(null);
  };

  return (
    <GallerySection>
      <h3 style={{ marginTop: 0 }}>Product Gallery PRO</h3>

      {!safeImages.length ? (
        <p style={{ opacity: 0.75 }}>No images.</p>
      ) : (
        <GalleryGrid>
          <MainStage>
            <StageTopRow>
              <StageChip>
                <ChipDot />
                <span style={{ fontWeight: 900 }}>Preview</span>
                <span style={{ opacity: 0.75 }}>
                  {index + 1}/{safeImages.length}
                </span>
              </StageChip>
              <StageActions>
                <IconBtn
                  onClick={(e) => {
                    e.stopPropagation();
                    openLb(index);
                  }}
                  title="Fullscreen"
                >
                  ⤢
                </IconBtn>
              </StageActions>
            </StageTopRow>

            <MainStageInner onClick={() => openLb(index)} aria-label="Open gallery">
              <MainImage src={activeImg} alt="product" loading="lazy" />
            </MainStageInner>
          </MainStage>

          <ThumbsRail>
            {safeImages.map((img, i) => (
              <ThumbBtn
                key={img + i}
                active={i === index}
                onClick={() => setIndex(i)}
                aria-label={`Image ${i + 1}`}
              >
                <ThumbImg src={img} alt={`thumb-${i + 1}`} loading="lazy" />
              </ThumbBtn>
            ))}
          </ThumbsRail>
        </GalleryGrid>
      )}

      {lightbox && safeImages.length > 0 && (
        <LightboxOverlay
          onClick={closeLb}
          onWheel={handleWheel}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <LightboxFrame onClick={(e) => e.stopPropagation()}>
            <LbTop>
              <LbTitle>
                <span style={{ color: "#00f7d2" }}>●</span>
                Gallery
                <span style={{ opacity: 0.7, fontSize: 12 }}>
                  {index + 1}/{safeImages.length}
                </span>
              </LbTitle>
              <LbControls>
                <IconBtn onClick={zoomOut} title="Zoom out">−</IconBtn>
                <IconBtn onClick={zoomIn} title="Zoom in">+</IconBtn>
                <IconBtn onClick={resetZoom} title="Reset zoom">↺</IconBtn>
                <IconBtn onClick={prev} title="Prev">‹</IconBtn>
                <IconBtn onClick={next} title="Next">›</IconBtn>
                <IconBtn onClick={closeLb} title="Close">✕</IconBtn>
              </LbControls>
            </LbTop>

            <LbStage>
              <LbArrow side="l" onClick={prev} aria-label="Previous">‹</LbArrow>
              <LbImage
                src={activeImg}
                alt="product-full"
                style={{ transform: `scale(${zoom})`, transition: "transform .12s ease-out" }}
                onDoubleClick={() => setZoom((z) => (z === 1 ? 1.8 : 1))}
              />
              <LbArrow side="r" onClick={next} aria-label="Next">›</LbArrow>
            </LbStage>

            <LbThumbs>
              {safeImages.map((img, i) => (
                <ThumbBtn
                  key={`lb-${img}-${i}`}
                  active={i === index}
                  onClick={() => {
                    setIndex(i);
                    setZoom(1);
                  }}
                  style={{ width: 88, height: 64, flex: "0 0 auto" }}
                >
                  <ThumbImg src={img} alt={`lb-thumb-${i + 1}`} />
                </ThumbBtn>
              ))}
            </LbThumbs>
          </LightboxFrame>
        </LightboxOverlay>
      )}
    </GallerySection>
  );
}

function AuctionPRO({ productData, onProductDataUpdate }) {
  const history = useHistory();
  const [auctionData, setAuctionData] = useState(null);
  const [showTierTable, setShowTierTable] = useState(false);
  const [showAuctionSidebar, setShowAuctionSidebar] = useState(false);
  const [isEditingAuction, setIsEditingAuction] = useState(false);
  const [auctionStartDateTime, setAuctionStartDateTime] = useState("");
  const [auctionEndDateTime, setAuctionEndDateTime] = useState("");
  const [auctionDescription, setAuctionDescription] = useState("");
  const [pricingTiers, setPricingTiers] = useState([]);
  const [isDeletingAuction, setIsDeletingAuction] = useState(false);
  const [isForceStoppingAuction, setIsForceStoppingAuction] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmModalConfig, setConfirmModalConfig] = useState(null);
  const [isDeletingProduct, setIsDeletingProduct] = useState(false);
  const [tierMinQuantity, setTierMinQuantity] = useState("");
  const [tierMaxQuantity, setTierMaxQuantity] = useState("");
  const [tierPrice, setTierPrice] = useState("");
  const [editingTierIndex, setEditingTierIndex] = useState(null);
  const [editTierMax, setEditTierMax] = useState("");
  const [editTierPrice, setEditTierPrice] = useState("");
  const [isCreatingAuction, setIsCreatingAuction] = useState(false);
  const [orders, setOrders] = useState(0);
  const [timeLeft, setTimeLeft] = useState({ hours: 0, minutes: 0, seconds: 0 });
  const [isEnded, setIsEnded] = useState(false);
  const [auctionTab, setAuctionTab] = useState('auction');
  const [biddingDetails, setBiddingDetails] = useState([]);
  const [isLoadingBids, setIsLoadingBids] = useState(false);
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [deliveryCountry, setDeliveryCountry] = useState('');
  const [countries, setCountries] = useState([]);
  const [secondDeliveryAddress, setSecondDeliveryAddress] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [bidQuantity, setBidQuantity] = useState(1);
  const [isSubmittingBid, setIsSubmittingBid] = useState(false);
  const [activeTierPrice, setActiveTierPrice] = useState(null);
  const [showBidSidebar, setShowBidSidebar] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('virtual_wallet');
  const [auctionFee, setAuctionFee] = useState(0);
  const [saleTaxFee, setSaleTaxFee] = useState(0);
  const [deliveryCharges, setDeliveryCharges] = useState(0);

  // Initialize countries list from local JSON file
  useEffect(() => {
    if (countriesData && Array.isArray(countriesData)) {
      setCountries(countriesData);
    }
  }, []);
  const fetchSaleTaxFee = async () => {
    if (!productData?._id) {
      setSaleTaxFee(0);
      return;
    }

    try {
      const requestData = {
        type: "sale_tax",
      };
      const response = await getExternalStoreFee(requestData);

      if (response.data) {
        const taxFee = response?.data?.data?.fee || 0;
        setSaleTaxFee(taxFee);
      } else {
        console.error("getFee:", response.statusText);
        setSaleTaxFee(0);
      }
    } catch (error) {
      console.error("Error fetching sale tax fee:", error);
      setSaleTaxFee(0);
    }
  };

  // Fetch auction fee when bid sidebar opens or quantity changes
  useEffect(() => {
    const fetchAuctionFee = async () => {
      if (!productData?._id || !bidQuantity || bidQuantity <= 0) {
        setAuctionFee(0);
        return;
      }

      try {
        const requestData = {
          type: "auction_fee",
          // categoryId: productData?.category_id,
        };
        const response = await getExternalStoreFee(requestData);

        if (response.data) {
          const fee = response?.data?.data?.fee || 0;
          setAuctionFee(fee);
        } else {
          console.error("getFee:", response.statusText);
          setAuctionFee(0);
        }
      } catch (error) {
        console.error("Error fetching auction fee:", error);
        setAuctionFee(0);
      }
    };

    if (showBidSidebar && bidQuantity > 0) {
      fetchAuctionFee();
      fetchSaleTaxFee()
    }
  }, [showBidSidebar, bidQuantity, productData?._id, productData?.category_id]);



  // Set delivery charges from productData
  useEffect(() => {
    if (productData?.deliveryCharges !== undefined) {
      setDeliveryCharges(parseFloat(productData.deliveryCharges) || 0);
    }
  }, [productData?.deliveryCharges]);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedAuction, setSelectedAuction] = useState(null);
  const [selectedAuctionCal, setSelectedAuctionCal] = useState(null);
  const [activeTierId, setActiveTierId] = useState(null);
  const [showMyOrders, setShowMyOrders] = useState(false);
  const [tierSummary, setTierSummary] = useState([]);
  const { socket } = useChainContext();
  const isSubmittingBidRef = useRef(false);

  const { coinList: virtualWalletCoins } = useSelector(state => ({
    coinList: state.coinList1?.data || [],
  }));

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const currentUserId = user?.data?.user?.user_id || user?.data?.user?._id || user?.data?.user?.id;
  const senderWallet = user?.data?.user?.address || user?.address || '';

  // Filter active_auction to only include auctions where end time has not passed
  const now = Date.now();
  const activeAuctions = productData?.active_auction?.filter(auction => {
    const endTime = auction?.acution_end_time || auction?.auction_end_time;
    if (!endTime) return false;
    const endDate = new Date(endTime).getTime();
    return endDate > now; // Only include if end time is in the future
  }) || [];
  const activeAuction = activeAuctions[0] || null;
  const auctionId = activeAuction?._id || null;
  const auctionTiers = activeAuction?.auction_tier || [];
  const activeAuctionEndTime = activeAuction?.acution_end_time || activeAuction?.auction_end_time;
  const activeAuctionStartTime = activeAuction?.auction_start_time
    ? new Date(activeAuction.auction_start_time).getTime()
    : null;
  const isUpcomingAuction = !!activeAuctionStartTime && now < activeAuctionStartTime;

  const isAuctionOwner = currentUserId === productData?.userId;
  const isProductOwner = currentUserId === productData?.userId;

  // Calculate default quantity: last tier minQty - aggregated quantity
  useEffect(() => {
    if (auctionTiers.length > 0 && orders !== undefined) {
      // Get the last tier (highest minQty)
      const sortedTiers = [...auctionTiers].sort((a, b) => {
        const aMin = a.minQty || a.min || 0;
        const bMin = b.minQty || b.min || 0;
        return bMin - aMin; // Sort descending
      });
      const lastTier = sortedTiers[0];
      const defaultQty = lastTier.minQty - orders;
      setBidQuantity(defaultQty);
    }
  }, [auctionTiers, orders]);

  useEffect(() => {
    if (!activeAuctionEndTime) {
      setTimeLeft({ hours: 0, minutes: 0, seconds: 0 });
      setIsEnded(true);
      return;
    }

    const updateCountdown = () => {
      const endDate = new Date(activeAuctionEndTime);
      const now = Date.now();
      const diff = Math.max(0, endDate.getTime() - now);

      if (diff <= 0) {
        setTimeLeft({ hours: 0, minutes: 0, seconds: 0 });
        setIsEnded(true);
        return;
      }

      setIsEnded(false);
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setTimeLeft({ hours, minutes, seconds });
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [activeAuctionEndTime]);

  const activeTier = auctionTiers.length > 0
    ? auctionTiers
      .slice()
      .reverse()
      .find((t) => orders >= (t.minQty || t.min)) || auctionTiers[0]
    : null;

  const maxTierQty = auctionTiers.length > 0
    ? Math.max(...auctionTiers.map(t => t.maxQty || 0))
    : 0;
  const progress = maxTierQty > 0 ? Math.min((orders / maxTierQty) * 100, 100) : 0;

  // Bid payment sidebar (PaymentSidebar 3-step) – orderDetail, products, total for current quantity (incl. VAT)
  const bidPaymentProps = useMemo(() => {
    const qty = bidQuantity && Number(bidQuantity) >= 1 ? Number(bidQuantity) : 0;
    if (qty <= 0 || !auctionTiers?.length || !productData?._id) {
      return { orderDetail: [], products: [], bidTotalAmount: null, tierPrice: 0 };
    }
    const totalQuantity = orders + qty;
    const sortedTiers = [...auctionTiers].sort((a, b) => (b.minQty || b.min || 0) - (a.minQty || a.min || 0));
    const selectedTier = sortedTiers.find((t) => totalQuantity >= (t.minQty || t.min || 0)) || auctionTiers[0];
    const tierPrice = parseFloat(selectedTier?.price || 0);
    const basePrice = tierPrice * qty;
    const vatPct = Number(productData?.vat ?? 0);
    const vatAmount = (vatPct / 100) * basePrice;
    const taxAmount = parseFloat(saleTaxFee || 0);
    const deliveryAmount = parseFloat(deliveryCharges || 0);
    const feeAmount = parseFloat(auctionFee || 0);
    const totalPrice = basePrice + taxAmount + deliveryAmount + feeAmount + vatAmount;
    const orderDetail = [{ product_id: productData._id, quantity: qty, price: tierPrice, vat: vatPct }];
    const products = [{ id: productData._id, name: productData.title || productData.name || 'Product', price: tierPrice, vat: vatPct }];
    return { orderDetail, products, bidTotalAmount: totalPrice, tierPrice };
  }, [bidQuantity, auctionTiers, orders, productData?._id, productData?.title, productData?.name, productData?.vat, saleTaxFee, deliveryCharges, auctionFee]);

  const handleReceivedAuctionDetail = useCallback((response) => {
    setIsLoadingBids(false);

    if (response?.tier_id && response?.price !== undefined && response?.quantity !== undefined) {
      setTierSummary(response.tier_summary);
      setOrders(response.quantity);
      setActiveTierPrice(response.price);
      setActiveTierId(response.tier_id);
      return;
    }

    if (response?.data && Array.isArray(response.data) && response.data.length > 0) {
      const auctionDataFromResponse = response.data.find(auction => auction._id === activeAuction?._id) || response.data[0];
      if (auctionDataFromResponse) {
        setAuctionData(auctionDataFromResponse);
        if (auctionDataFromResponse?.bidding_details) {
          setBiddingDetails(auctionDataFromResponse.bidding_details);
        }
      }
    } else if (response?.data) {
      setAuctionData(response.data);
      if (response.data?.bidding_details) {
        setBiddingDetails(response.data.bidding_details);
      }
    }
  }, [activeAuction?._id]);

  useEffect(() => {
    if (!socket || !activeAuction?._id) return;

    if (auctionTab === 'history') {
      setIsLoadingBids(true);

      if (socket.connected) {
        socket.emit('fetch_auction_detail', {
          auction_id: activeAuction._id
        });
      }
    }
  }, [auctionTab, activeAuction?._id, socket]);

  const formatUserId = (userId) => {
    if (!userId) return 'N/A';
    if (typeof userId === 'string' && userId.length > 10) {
      return `${userId.substring(0, 6)}...${userId.substring(userId.length - 4)}`;
    }
    return userId;
  };

  const handleAddTier = () => {
    if (!tierPrice) {
      toast.error("Please enter price");
      return;
    }
    const price = Number(tierPrice);
    if (price <= 0) {
      toast.error("Price must be greater than 0");
      return;
    }

    // Always require max quantity input
    if (!tierMaxQuantity || Number(tierMaxQuantity) <= 0) {
      toast.error("Please enter max quantity");
      return;
    }

    let minQty, maxQty;

    if (pricingTiers.length === 0) {
      // First tier: Min = 1, Max = user input
      minQty = 1;
      maxQty = Number(tierMaxQuantity);
      
      // Validate that max is at least 1
      if (maxQty < 1) {
        toast.error("Max quantity must be at least 1");
        return;
      }
    } else {
      // Subsequent tiers: Min = previous tier max + 1, Max = user input
      const previousTierMax = pricingTiers[pricingTiers.length - 1].maxQty;
      minQty = previousTierMax + 1;
      maxQty = Number(tierMaxQuantity);

      // Validate that max is greater than min
      if (maxQty <= minQty) {
        toast.error(`Max quantity must be greater than ${minQty}`);
        return;
      }
    }

    setPricingTiers([...pricingTiers, { minQty, maxQty, price }]);
    setTierPrice("");
    setTierMaxQuantity(""); // Reset max quantity input
  };

  const handleRemoveTier = (index) => {
    setPricingTiers(pricingTiers.filter((_, i) => i !== index));
    // Reset max quantity input when removing a tier
    setTierMaxQuantity("");
    // Cancel editing if the removed tier was being edited
    if (editingTierIndex === index) {
      setEditingTierIndex(null);
    } else if (editingTierIndex > index) {
      // Adjust editing index if a tier before the editing one was removed
      setEditingTierIndex(editingTierIndex - 1);
    }
  };

  const handleEditTier = (index) => {
    const tier = pricingTiers[index];
    setEditingTierIndex(index);
    setEditTierMax(tier.maxQty.toString());
    setEditTierPrice(tier.price.toString());
  };

  const handleSaveTier = (index) => {
    const tier = pricingTiers[index];
    
    if (!editTierMax || Number(editTierMax) <= 0) {
      toast.error("Please enter a valid max quantity");
      return;
    }
    
    if (!editTierPrice || Number(editTierPrice) <= 0) {
      toast.error("Please enter a valid price");
      return;
    }

    const newMaxQty = Number(editTierMax);
    const newPrice = Number(editTierPrice);
    const minQty = tier.minQty;

    // Validate that max is greater than min
    if (newMaxQty <= minQty) {
      toast.error(`Max quantity must be greater than ${minQty}`);
      return;
    }

    // If editing a tier that's not the last one, check if it affects subsequent tiers
    if (index < pricingTiers.length - 1) {
      const nextTier = pricingTiers[index + 1];
      const nextTierMin = nextTier.minQty;
      
      // If new max + 1 is greater than next tier's min, we need to update next tier's min
      if (newMaxQty + 1 !== nextTierMin) {
        // Update the tier
        const updatedTiers = [...pricingTiers];
        updatedTiers[index] = { ...tier, maxQty: newMaxQty, price: newPrice };
        
        // Recalculate subsequent tiers' min values
        for (let i = index + 1; i < updatedTiers.length; i++) {
          const prevTierMax = updatedTiers[i - 1].maxQty;
          updatedTiers[i] = {
            ...updatedTiers[i],
            minQty: prevTierMax + 1
          };
        }
        
        setPricingTiers(updatedTiers);
      } else {
        // Just update this tier
        const updatedTiers = [...pricingTiers];
        updatedTiers[index] = { ...tier, maxQty: newMaxQty, price: newPrice };
        setPricingTiers(updatedTiers);
      }
    } else {
      // Last tier, just update it
      const updatedTiers = [...pricingTiers];
      updatedTiers[index] = { ...tier, maxQty: newMaxQty, price: newPrice };
      setPricingTiers(updatedTiers);
    }

    setEditingTierIndex(null);
    setEditTierMax("");
    setEditTierPrice("");
  };

  const handleCancelEdit = () => {
    setEditingTierIndex(null);
    setEditTierMax("");
    setEditTierPrice("");
  };

  /**
   * @param {Object} [deliveryOverride] - When provided (e.g. from PaymentSidebar), use these for delivery fields instead of state.
   * Shape: { deliveryAddress, deliveryCountry, secondDeliveryAddress?, phoneNumber }
   * @returns {boolean} - true if the socket emit was called, false if validation failed and returned early.
   */
  const handlePlaceBid = (deliveryOverride) => {
    console.log('[PlaceBid] handlePlaceBid called', { deliveryOverride: !!deliveryOverride, bidQuantity, socketConnected: socket?.connected, activeAuctionId: activeAuction?._id });
    const addr = deliveryOverride?.deliveryAddress ?? deliveryAddress;
    const country = deliveryOverride?.deliveryCountry ?? deliveryCountry;
    const secondAddr = deliveryOverride?.secondDeliveryAddress ?? secondDeliveryAddress;
    const phone = deliveryOverride?.phoneNumber ?? phoneNumber;

    if (!bidQuantity || bidQuantity <= 0) {
      console.log('[PlaceBid] validation failed: invalid quantity', bidQuantity);
      toast.error('Please enter a valid quantity', {
        position: toast.POSITION.TOP_RIGHT,
      });
      return false;
    }

    // Check if quantity exceeds maximum allowed
    const availableQty = maxTierQty > 0 ? maxTierQty - orders : Infinity;
    if (bidQuantity > availableQty) {
      toast.error(`Maximum quantity allowed is ${availableQty}. You cannot enter more than the available quantity.`, {
        position: toast.POSITION.TOP_RIGHT,
      });
      return false;
    }

    if (!addr || addr.trim() === '') {
      toast.error('Please enter a delivery address', {
        position: toast.POSITION.TOP_RIGHT,
      });
      return false;
    }
    if (!country || country.trim() === '') {
      toast.error('Please enter a delivery country', {
        position: toast.POSITION.TOP_RIGHT,
      });
      return false;
    }
    if (!phone || phone.trim() === '') {
      toast.error('Please enter a phone number', {
        position: toast.POSITION.TOP_RIGHT,
      });
      return false;
    }

    if (!socket || !socket.connected) {
      console.log('[PlaceBid] validation failed: socket not connected', { hasSocket: !!socket });
      toast.error('Socket not connected. Please try again.', {
        position: toast.POSITION.TOP_RIGHT,
      });
      return false;
    }

    if (!activeAuction?._id) {
      console.log('[PlaceBid] validation failed: no activeAuction id');
      toast.error('Auction ID is missing', {
        position: toast.POSITION.TOP_RIGHT,
      });
      return false;
    }

    if (!currentUserId) {
      toast.error('User not logged in', {
        position: toast.POSITION.TOP_RIGHT,
      });
      return false;
    }

    const selectedTier = auctionTiers
      .slice()
      .sort((a, b) => (b.minQty || b.min || 0) - (a.minQty || a.min || 0))
      .find((t) => bidQuantity >= (t.minQty || t.min || 0)) || auctionTiers[0];

    if (selectedTier && productData?.symbol) {
      const symbol = productData.symbol.toUpperCase();
      const pricePerUnit = parseFloat(selectedTier.price || 0);
      const basePrice = pricePerUnit * bidQuantity;
      
      // Calculate additional costs: tax + delivery charges + fee
      const taxAmount = parseFloat(saleTaxFee || 0);
      const deliveryAmount = parseFloat(deliveryCharges || 0);
      const feeAmount = parseFloat(auctionFee || 0);
      const totalPrice = basePrice + taxAmount + deliveryAmount + feeAmount;

      const userCoin = virtualWalletCoins.find(
        (coin) => (coin.symbol || '').toUpperCase() === symbol
      );

      const userBalance = parseFloat(userCoin?.quantity || 0);

      if (userBalance < totalPrice) {
        toast.error(`Insufficient balance. You have ${userBalance} ${symbol}, but need ${totalPrice} ${symbol} (Base: ${basePrice} + Tax: ${taxAmount} + Delivery: ${deliveryAmount} + Fee: ${feeAmount})`, {
          position: toast.POSITION.TOP_RIGHT,
        });
        return false;
      }
    }

    setIsSubmittingBid(true);
    isSubmittingBidRef.current = true;
    const socketPayload = {
      user_id: currentUserId,
      senderWallet: senderWallet,
      quantity: bidQuantity,
      auction_id: activeAuction._id,
      delivery_address: addr,
      delivery_country: country,
      second_delivery_address: secondAddr || '',
      phone_number: phone,
    };
    console.log("🚀 ~ handlePlaceBid ~ socketPayload:", socketPayload)

    console.log('[PlaceBid] emitting add-auction-bid', { payload: socketPayload, event: 'add-auction-bid' });
    socket.emit('add-auction-bid', socketPayload);
    setIsSubmittingBid(false);

    console.log('[PlaceBid] socket.emit done, waiting for add-auction-bid-response');
    return true;
  };

  const handleEditAuction = () => {
    if (!activeAuction) {
      toast.error("No auction to edit");
      return;
    }

    setIsEditingAuction(true);
    setAuctionStartDateTime(
      activeAuction.auction_start_time
        ? new Date(activeAuction.auction_start_time).toISOString().slice(0, 16)
        : ""
    );
    setAuctionEndDateTime(
      activeAuction.auction_end_time || activeAuction.acution_end_time
        ? new Date(activeAuction.auction_end_time || activeAuction.acution_end_time).toISOString().slice(0, 16)
        : ""
    );
    setAuctionDescription(activeAuction.description || "");
    
    // Convert auction tiers to pricing tiers format
    const tiers = (activeAuction.auction_tier || []).map(tier => ({
      minQty: tier.minQty || tier.min || 0,
      maxQty: tier.maxQty || null,
      price: tier.price || 0
    }));
    setPricingTiers(tiers);
    
    setShowAuctionSidebar(true);
  };

  const handleCreateAuction = async () => {
    if (!auctionStartDateTime) {
      toast.error("Please select a start date and time");
      return;
    }
    if (!auctionEndDateTime) {
      toast.error("Please select an end date and time");
      return;
    }
    if (!auctionDescription || auctionDescription.trim() === "") {
      toast.error("Please enter a description");
      return;
    }
    if (pricingTiers.length === 0) {
      toast.error("Please add at least one pricing tier");
      return;
    }
    if (!productData || !productData._id) {
      toast.error("Product data is missing");
      return;
    }

    const startDateTime = new Date(auctionStartDateTime);
    const endDateTime = new Date(auctionEndDateTime);

    if (endDateTime <= startDateTime) {
      toast.error("End date and time must be after start date and time");
      return;
    }

    setIsCreatingAuction(true);
    try {
      const payload = {
        product_id: productData._id,
        auction_start_time: startDateTime.toISOString(),
        auction_end_time: endDateTime.toISOString(),
        description: auctionDescription,
        tier_details: pricingTiers.map(tier => ({
          minQty: tier.minQty,
          maxQty: tier.maxQty,
          price: tier.price
        }))
      };

      let response;
      if (isEditingAuction && activeAuction?._id) {
        // Edit existing auction
        payload.auction_id = activeAuction._id;
        response = await editProductAuction(payload);
      } else {
        // Create new auction
        response = await createProductAuction(payload);
      }

      if (response.data?.success || response.data?.status) {
        toast.success(response.data?.message || (isEditingAuction ? "Auction updated successfully!" : "Auction created successfully!"));
        setShowAuctionSidebar(false);
        setIsEditingAuction(false);
        setAuctionStartDateTime("");
        setAuctionEndDateTime("");
        setAuctionDescription("");
        setPricingTiers([]);
        setTierPrice("");

        if (onProductDataUpdate) {
          onProductDataUpdate();
        }
      } else {
        toast.error(response.data?.message || (isEditingAuction ? "Failed to update auction" : "Failed to create auction"));
      }
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || (isEditingAuction ? "Failed to update auction" : "Failed to create auction"));
    } finally {
      setIsCreatingAuction(false);
    }
  };

  const handleForceStopAuction = () => {
    if (!activeAuction?._id || !productData?._id) {
      toast.error("Auction data is missing");
      return;
    }

    setConfirmModalConfig({
      title: "Force Stop Auction",
      message: "Are you sure you want to force stop this auction? This action cannot be undone.",
      onConfirm: async () => {
        setIsForceStoppingAuction(true);
        setShowConfirmModal(false);
        try {
          const payload = {
            product_id: productData._id,
            auction_id: activeAuction._id
          };

          const response = await forceStopAuction(payload);

          if (response.data?.success || response.data?.status) {
            toast.success(response.data?.message || "Auction force stopped successfully!");
            if (onProductDataUpdate) {
              onProductDataUpdate();
            }
          } else {
            toast.error(response.data?.message || "Failed to force stop auction");
          }
        } catch (error) {
          toast.error(error.response?.data?.message || error.message || "Failed to force stop auction");
        } finally {
          setIsForceStoppingAuction(false);
        }
      },
      onCancel: () => {
        setShowConfirmModal(false);
        setConfirmModalConfig(null);
      }
    });
    setShowConfirmModal(true);
  };

  const handleDeleteAuction = () => {
    if (!activeAuction?._id || !productData?._id) {
      toast.error("Auction data is missing");
      return;
    }

    setConfirmModalConfig({
      title: "Delete Auction",
      message: "Are you sure you want to delete this auction? This action cannot be undone.",
      onConfirm: async () => {
        setIsDeletingAuction(true);
        setShowConfirmModal(false);
        try {
          const payload = {
            product_id: productData._id,
            auction_id: activeAuction._id
          };

          const response = await deleteProductAuction(payload);

          if (response.data?.success || response.data?.status) {
            toast.success(response.data?.message || "Auction deleted successfully!");
            if (onProductDataUpdate) {
              onProductDataUpdate();
            }
          } else {
            toast.error(response.data?.message || "Failed to delete auction");
          }
        } catch (error) {
          toast.error(error.response?.data?.message || error.message || "Failed to delete auction");
        } finally {
          setIsDeletingAuction(false);
        }
      },
      onCancel: () => {
        setShowConfirmModal(false);
        setConfirmModalConfig(null);
      }
    });
    setShowConfirmModal(true);
  };

  const [showProductEditSidebar, setShowProductEditSidebar] = useState(false);
  const [productEditForm, setProductEditForm] = useState({
    title: "",
    brand: "",
    description: "",
    warrantyInMonths: 0,
    deliveryCharges: 0,
    pickupAddress: "",
  });
  const [isUpdatingProduct, setIsUpdatingProduct] = useState(false);

  const handleEditProduct = () => {
    if (!productData?._id) {
      toast.error("Product data is missing");
      return;
    }
    // Pre-populate form with current product data
    setProductEditForm({
      title: productData.title || "",
      brand: productData.brand || "",
      description: productData.description || "",
      warrantyInMonths: productData.warrantyInMonths || productData.warranty || 0,
      deliveryCharges: productData.deliveryCharges || 0,
      pickupAddress: productData.pickupAddress || "",
    });
    setShowProductEditSidebar(true);
  };

  const handleUpdateProduct = async () => {
    if (!productData?._id) {
      toast.error("Product data is missing");
      return;
    }

    if (!productEditForm.title || productEditForm.title.trim() === "") {
      toast.error("Please enter a product title");
      return;
    }

    setIsUpdatingProduct(true);
    try {
      const formData = new FormData();
      formData.append("product_id", productData._id);
      formData.append("title", productEditForm.title);
      if (productEditForm.brand) formData.append("brand", productEditForm.brand);
      if (productEditForm.description) formData.append("description", productEditForm.description);
      if (productEditForm.warrantyInMonths) formData.append("warrantyInMonths", productEditForm.warrantyInMonths);
      if (productEditForm.deliveryCharges !== undefined) formData.append("deliveryCharges", productEditForm.deliveryCharges);
      if (productEditForm.pickupAddress) formData.append("pickupAddress", productEditForm.pickupAddress);

      const response = await updateProduct(formData);

      if (response.data?.success || response.data?.status || response.data?.Message) {
        toast.success(response.data?.Message || response.data?.message || "Product updated successfully!");
        setShowProductEditSidebar(false);
        if (onProductDataUpdate) {
          onProductDataUpdate();
        }
      } else {
        toast.error(response.data?.Message || response.data?.message || "Failed to update product");
      }
    } catch (error) {
      toast.error(error.response?.data?.Message || error.response?.data?.message || error.message || "Failed to update product");
    } finally {
      setIsUpdatingProduct(false);
    }
  };

  const handleDeleteProduct = () => {
    if (!productData?._id) {
      toast.error("Product data is missing");
      return;
    }

    setConfirmModalConfig({
      title: "Delete Product",
      message: "Are you sure you want to delete this product? This action cannot be undone.",
      onConfirm: async () => {
        setIsDeletingProduct(true);
        setShowConfirmModal(false);
        try {
          const payload = {
            product_id: productData._id
          };

          const response = await deleteMyProduct(payload);

          if (response.data?.success || response.data?.status || response.data?.Message) {
            toast.success(response.data?.Message || response.data?.message || "Product deleted successfully!");
            if (onProductDataUpdate) {
              onProductDataUpdate();
            }
            // Redirect to products page or home after deletion
            setTimeout(() => {
              history.push('/products');
            }, 1000);
          } else {
            toast.error(response.data?.Message || response.data?.message || "Failed to delete product");
          }
        } catch (error) {
          toast.error(error.response?.data?.Message || error.response?.data?.message || error.message || "Failed to delete product");
        } finally {
          setIsDeletingProduct(false);
        }
      },
      onCancel: () => {
        setShowConfirmModal(false);
        setConfirmModalConfig(null);
      }
    });
    setShowConfirmModal(true);
  };

  // Check if auction exists and has tiers
  const hasAuction = activeAuction && auctionTiers.length > 0;
  // Check if auction is still active (not ended)
  const hasActiveAuction = hasAuction && !isEnded;

  const handleBidResponse = useCallback((res) => {
    console.log('[PlaceBid] handleBidResponse (global listener) received', { res, message: res?.message, dataLength: res?.data?.length });
    setAuctionData(res.data[0]);

    if (res?.message) {
      const wasSubmitting = isSubmittingBidRef.current;
      setIsSubmittingBid(false);
      isSubmittingBidRef.current = false;

      if (wasSubmitting) {
        if (res.message === 'Insufficient balance' || res.message?.toLowerCase().includes('insufficient')) {
          toast.error(res.message || 'Insufficient balance', {
            position: toast.POSITION.TOP_RIGHT,
          });
          return;
        }

        toast.success(res.message, {
          position: toast.POSITION.TOP_RIGHT,
        });
        // Close sidebar on successful bid
        setShowBidSidebar(false);
        // Reset form fields
        setBidQuantity(1);
        setDeliveryAddress('');
        setDeliveryCountry('');
        setSecondDeliveryAddress('');
        setPhoneNumber('');
        setPaymentMethod('virtual_wallet');
        return;
      }

      return;
    }

    if (res?.data && Array.isArray(res.data) && res.data.length > 0) {
      const auctionDataFromResponse = res.data.find(auction => auction._id === auctionId) || res.data[0];
      if (auctionDataFromResponse) {
        setAuctionData(auctionDataFromResponse);
        if (auctionDataFromResponse?.bidding_details) {
          setBiddingDetails(auctionDataFromResponse.bidding_details);
        }
      }
      setIsSubmittingBid(false);
      isSubmittingBidRef.current = false;
    } else if (res?.data) {
      setAuctionData(res.data);
      if (res.data?.bidding_details) {
        setBiddingDetails(res.data.bidding_details);
      }
      setIsSubmittingBid(false);
      isSubmittingBidRef.current = false;
    } else {
      setIsSubmittingBid(false);
      isSubmittingBidRef.current = false;
    }
  }, [auctionId]);

  useEffect(() => {
    if (!socket || !auctionId) return;

    socket.emit("auction-detail", {
      auction_id: auctionId,
    });
    if (socket.connected) {
      socket.emit('fetch_auction_detail', {
        auction_id: auctionId
      });
    }
    const fetchAuctionDetail = () => {
      if (socket.connected) {
        socket.emit('fetch_auction_detail', {
          auction_id: auctionId
        });
      }
    };

    fetchAuctionDetail();

    socket.on('connect', fetchAuctionDetail);
    socket.on("add-auction-bid-response", handleBidResponse);
    socket.on('received_auction_detail', handleReceivedAuctionDetail);

    return () => {
      if (socket) {
        socket.off('connect', fetchAuctionDetail);
        socket.off('received_auction_detail', handleReceivedAuctionDetail);
        socket.off("add-auction-bid-response", handleBidResponse);
      }
    };
  }, [socket, auctionId, handleBidResponse, handleReceivedAuctionDetail]);

  // Extract product images
  const productImages = productData?.productImages || productData?.images || productData?.pictures || [];

  return (
    <>
      {/* <GalleryPRO images={productImages} /> */}
      <Section className="mt-4">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
          <h3 style={{ display: "flex", alignItems: "center", gap: 10, margin: 0 }}>
            Auction Mode PRO <Led />
          </h3>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            {hasAuction && activeAuctionEndTime && !isEnded && (
              <CountdownChip status="live">
                ⏳ Ends
                <span style={{ letterSpacing: 0.4 }}>
                  {`${String(timeLeft.hours).padStart(2, '0')}h ${String(timeLeft.minutes).padStart(2, '0')}m ${String(timeLeft.seconds).padStart(2, '0')}s`}
                </span>
              </CountdownChip>
            )}
            {hasAuction && isEnded && (
              <CountdownChip status="ended">
                ⛔ Ended
              </CountdownChip>
            )}
          {(!hasAuction || isEnded) && isAuctionOwner && (
            <button
              onClick={() => setShowAuctionSidebar(true)}
              style={{
                padding: "10px 20px",
                borderRadius: 10,
                background: "linear-gradient(90deg, #00f7d2, #5ce9ff)",
                color: "#000",
                fontWeight: 700,
                cursor: "pointer",
                border: "none",
                fontSize: 14,
              }}
            >
              {isEnded ? "Create New Auction" : "Create Auction"}
            </button>
          )}
          </div>
        </div>

        {hasAuction ? (
          <>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <button
                onClick={() => setAuctionTab('auction')}
                style={{
                  padding: '8px 16px',
                  borderRadius: 8,
                  background: auctionTab === 'auction' ? 'linear-gradient(90deg, #00f7d2, #5ce9ff)' : 'transparent',
                  color: auctionTab === 'auction' ? '#000' : '#e5e7eb',
                  fontWeight: auctionTab === 'auction' ? 600 : 400,
                  cursor: 'pointer',
                  border: auctionTab === 'auction' ? 'none' : '1px solid #1f2937',
                  fontSize: 14,
                }}
              >
                Auction
              </button>
              <button
                onClick={() => setAuctionTab('history')}
                style={{
                  padding: '8px 16px',
                  borderRadius: 8,
                  background: auctionTab === 'history' ? 'linear-gradient(90deg, #00f7d2, #5ce9ff)' : 'transparent',
                  color: auctionTab === 'history' ? '#000' : '#e5e7eb',
                  fontWeight: auctionTab === 'history' ? 600 : 400,
                  cursor: 'pointer',
                  border: auctionTab === 'history' ? 'none' : '1px solid #1f2937',
                  fontSize: 14,
                }}
              >
                Order History
              </button>
              <button
                onClick={() => setAuctionTab('pastAuctions')}
                style={{
                  padding: '8px 16px',
                  borderRadius: 8,
                  background: auctionTab === 'pastAuctions' ? 'linear-gradient(90deg, #00f7d2, #5ce9ff)' : 'transparent',
                  color: auctionTab === 'pastAuctions' ? '#000' : '#e5e7eb',
                  fontWeight: auctionTab === 'pastAuctions' ? 600 : 400,
                  cursor: 'pointer',
                  border: auctionTab === 'pastAuctions' ? 'none' : '1px solid #1f2937',
                  fontSize: 14,
                }}
              >
                Past Auctions
              </button>
            </div>

            {auctionTab === 'auction' ? (
              <AuctionBox>
                {activeAuction.auction_start_time && activeAuctionEndTime && (
                  <ActionWindowCard style={{ marginBottom: 16 }}>
                    <ActionWindowLabel>Auction Window</ActionWindowLabel>
                    <DateRow>
                      {activeAuction.auction_start_time && (
                        <DateChip>
                          <DateDot color="#00f7d2" />
                          Start Date: {new Date(activeAuction.auction_start_time).toLocaleDateString('en-GB', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric'
                          })}
                        </DateChip>
                      )}
                      {activeAuctionEndTime && (
                        <DateChip>
                          <DateDot color="#fca5a5" />
                          End Date: {new Date(activeAuctionEndTime).toLocaleDateString('en-GB', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric'
                          })}
                        </DateChip>
                      )}
                    </DateRow>
                  </ActionWindowCard>
                )}
                {activeAuction?.description && (
                  <PurchaseDescCard style={{ marginBottom: 16 }}>
                    <PurchaseDescLabel>
                      <span style={{ color: "#00f7d2", fontWeight: 900 }}>⚡</span>
                      Purchase description
                      <span
                        style={{
                          marginLeft: "auto",
                          fontSize: 11,
                          padding: "3px 8px",
                          borderRadius: 999,
                          border: "1px solid #1f2937",
                          background: "rgba(17,21,34,0.55)",
                          color: "#5ce9ff",
                          opacity: 0.92,
                        }}
                      >
                        preset
                      </span>
                    </PurchaseDescLabel>
                    <PurchaseDescText>
                      {activeAuction.description || "—"}
                    </PurchaseDescText>
                  </PurchaseDescCard>
                )}
                <p style={{ fontSize: 13, opacity: 0.8 }}>Time left:{" "}
                  <span style={{ color: "#5ce9ff" }}>
                    {isEnded ? 'Ended' : `${String(timeLeft.hours).padStart(2, '0')}h ${String(timeLeft.minutes).padStart(2, '0')}m ${String(timeLeft.seconds).padStart(2, '0')}s`}
                  </span>
                </p>
                <p>
                  Aggregated Quantity: <strong>{orders || 0}</strong>
                </p>
                {(activeTierPrice !== null || activeTier) && (
                  <p>
                    Active Tier Price: <strong>{activeTierPrice !== null ? activeTierPrice.toFixed(6) : (activeTier?.price?.toFixed(6) || activeTier?.price || '0')} {productData?.symbol || 'USDT'}{productData?.network_name ? ` (${productData.network_name})` : ''}</strong>
                  </p>
                )}


                {isEnded && (
                  <div style={{
                    padding: '0.75rem 1.5rem',
                    marginTop: '1rem',
                    borderRadius: '0.75rem',
                    background: 'rgba(239, 68, 68, 0.1)',
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                    color: '#ef4444',
                    textAlign: 'center',
                    fontSize: '0.875rem',
                    fontWeight: 600
                  }}>
                    This auction has ended. You can create a new auction using the button above.
                  </div>
                )}
                {!isEnded && !isAuctionOwner && (
                  isUpcomingAuction ? (
                    <div
                      style={{
                        padding: '0.75rem 1.5rem',
                        marginTop: '1rem',
                        borderRadius: '0.75rem',
                        background: 'rgba(56, 189, 248, 0.08)',
                        border: '1px solid rgba(56, 189, 248, 0.35)',
                        color: '#7dd3fc',
                        textAlign: 'center',
                        fontSize: '0.875rem',
                        fontWeight: 500,
                      }}
                    >
                      Auction has not started yet. You will be able to place bids once it starts.
                    </div>
                  ) : (
                    <button
                      onClick={() => {
                        setBidQuantity((q) => (q && q >= 1 ? q : 1));
                        setShowBidSidebar(true);
                      }}
                      style={{
                        padding: "12px 20px",
                        borderRadius: 10,
                        background: 'linear-gradient(90deg, #00f7d2, #5ce9ff)',
                        color: '#000',
                        fontWeight: 700,
                        cursor: 'pointer',
                        border: 'none',
                        fontSize: 14,
                        marginTop: 14,
                        width: '100%'
                      }}
                    >
                      Place Bid
                    </button>
                  )
                )}
                {!isEnded && isAuctionOwner && (
                  <div style={{
                    padding: '0.75rem 1.5rem',
                    marginTop: '1rem',
                    borderRadius: '0.75rem',
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: '#9ca3af',
                    textAlign: 'center',
                    fontSize: '0.875rem'
                  }}>
                    You cannot bid on your own auction
                  </div>
                )}

                {isAuctionOwner && hasActiveAuction && (
                  <div style={{ marginTop: 16, display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                    <button
                      onClick={handleEditAuction}
                      style={{ padding: '8px 12px', borderRadius: 10, background: '#0d1220', border: '1px solid #5ce9ff', color: '#5ce9ff', cursor: 'pointer', fontSize: '12px' }}>
                      Edit Auction
                    </button>
                    <button
                      onClick={handleForceStopAuction}
                      disabled={isForceStoppingAuction || isEnded}
                      style={{ 
                        padding: '8px 12px', 
                        borderRadius: 10, 
                        background: isForceStoppingAuction || isEnded ? '#1f2937' : '#0d1220', 
                        border: '1px solid #ef4444', 
                        color: isForceStoppingAuction || isEnded ? '#777' : '#ef4444', 
                        cursor: isForceStoppingAuction || isEnded ? 'not-allowed' : 'pointer',
                        fontSize: '12px'
                      }}>
                      {isForceStoppingAuction ? 'Stopping...' : 'Force Stop'}
                    </button>
                    <button
                      onClick={handleDeleteAuction}
                      disabled={isDeletingAuction}
                      style={{ 
                        padding: '8px 12px', 
                        borderRadius: 10, 
                        background: isDeletingAuction ? '#1f2937' : '#0d1220', 
                        border: '1px solid #ef4444', 
                        color: isDeletingAuction ? '#777' : '#ef4444', 
                        cursor: isDeletingAuction ? 'not-allowed' : 'pointer',
                        fontSize: '12px'
                      }}>
                      {isDeletingAuction ? 'Deleting...' : 'Delete Auction'}
                    </button>
                    <button
                      onClick={() => setOrders(o => Math.max(0, o - Number(document.getElementById('qtyInput')?.value || 1)))}
                      style={{ padding: '8px 12px', borderRadius: 10, background: '#111827', border: '1px solid #5ce9ff', color: '#5ce9ff', cursor: 'pointer', fontSize: '12px' }}>
                      Remove Last Order
                    </button>
                  <button
                    onClick={() => setOrders(0)}
                    style={{ padding: '8px 12px', borderRadius: 10, background: '#111827', border: '1px solid #00f7d2', color: '#00f7d2', cursor: 'pointer' }}>
                    Reset Auction
                  </button>
                  <button
                    onClick={() => alert('Tier Table modal coming soon')}
                    style={{ padding: '8px 12px', borderRadius: 10, background: '#0d1220', border: '1px solid #1f2937', color: '#e5e7eb', cursor: 'pointer' }}>
                    View Tiers
                  </button>
                  <button
                    onClick={() => setShowTierTable(v => !v)}
                    style={{ padding: '8px 12px', borderRadius: 10, background: '#0d1220', border: '1px solid #5ce9ff', color: '#5ce9ff', cursor: 'pointer' }}>
                    Tier Table PRO
                  </button>

                  <button disabled style={{ padding: '8px 12px', borderRadius: 10, background: '#1f2937', border: '1px solid #111827', color: '#777', cursor: 'not-allowed' }}>
                    My Contribution: {0}
                  </button>
                  </div>
                )}

                {showTierTable && (
                  <div style={{ marginTop: 16, padding: 14, border: '1px solid #1f2937', borderRadius: 12, background: 'rgba(0,247,210,0.05)' }}>
                    <h4 style={{ color: '#00f7d2', marginBottom: 10 }}>Tier Table PRO</h4>
                    {auctionTiers.map((t, i) => {
                      const minQty = t.minQty || t.min || 0;
                      const maxQty = t.maxQty || null;
                      const qtyRange = maxQty ? `${minQty}-${maxQty}` : `≥${minQty}`;
                      return (
                        <div key={i} style={{ padding: '6px 8px', marginBottom: 6, border: '1px solid #1f2937', borderRadius: 8, background: 'rgba(255,255,255,0.03)', fontSize: 13 }}>
                          Tier {i + 1}: {qtyRange} units → {t.price?.toFixed(6) || t.price} {productData?.symbol || '€'}
                        </div>
                      );
                    })}
                  </div>
                )}

                <div style={{ marginTop: 16 }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 10,
                      flexWrap: "wrap",
                    }}
                  >
                    <h4 style={{ margin: 0, color: "#5ce9ff" }}>Tier Grid</h4>
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                      <span
                        style={{
                          fontSize: 12,
                          opacity: 0.75,
                          padding: "4px 10px",
                          borderRadius: 999,
                          border: "1px solid #1f2937",
                          background: "#0d1220",
                        }}
                      >
                        👥 Participants
                      </span>
                      <span
                        style={{
                          fontSize: 12,
                          opacity: 0.75,
                          padding: "4px 10px",
                          borderRadius: 999,
                          border: "1px solid #1f2937",
                          background: "#0d1220",
                        }}
                      >
                        📦 Sold / Tier
                      </span>
                    </div>
                  </div>

                  <TierGridContainer>
                    {auctionTiers.map((t, idx) => {
                      const tierMin = t.minQty || t.min || 0;
                      const tierMax = t.maxQty || null;
                      const isActive = activeTierId && t._id === activeTierId;
                      const tierSummaryData = tierSummary?.find(ts => ts.tier_id === t._id);

                      const participants = Number(tierSummaryData?.total_participants || 0);
                      const sold = Number(tierSummaryData?.total_quantity || 0);

                      // Calculate total quantity sold across all tiers for share calculation
                      const totalQtySold = tierSummary?.reduce((sum, ts) => sum + (ts.total_quantity || 0), 0) || 0;
                      const share = totalQtySold > 0
                        ? Math.min(100, (sold / totalQtySold) * 100)
                        : 0;
                      const tierCapacity = tierMax && tierMin 
                        ? (tierMax - tierMin) + 1 
                        : (tierMax || maxTierQty);
                      const progressPercentage = tierCapacity > 0
                        ? Math.min(100, (sold / tierCapacity) * 100)
                        : 0;

                      return (
                        <TierCard key={idx} active={isActive}>
                          {isActive && (
                            <div
                              style={{
                                position: "absolute",
                                top: 10,
                                right: 10,
                                fontSize: 12,
                                padding: "4px 8px",
                                borderRadius: 999,
                                background: "rgba(0,247,210,0.18)",
                                border: "1px solid rgba(0,247,210,0.35)",
                                color: "#00f7d2",
                                fontWeight: 800,
                              }}
                            >
                              ACTIVE
                            </div>
                          )}

                          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10 }}>
                            <div>
                              <div style={{ fontSize: 12, opacity: 0.7 }}>Tier {idx + 1}</div>
                              <div style={{ fontSize: 18, fontWeight: 900, color: "#e5e7eb" }}>
                                {tierMin} – {tierMax || '∞'} units
                              </div>
                            </div>
                            <div style={{ textAlign: "left" }}>
                              <div style={{ fontSize: 12, opacity: 0.7 }}>Price</div>
                              <div style={{ fontSize: 18, fontWeight: 900, color: "#5ce9ff" }}>
                                {t.price?.toFixed(2) || t.price} {productData?.symbol || 'USDT'}{productData?.network_name ? ` (${productData.network_name})` : ''}
                              </div>
                            </div>
                          </div>

                          <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                            <div
                              style={{
                                padding: "10px 10px",
                                borderRadius: 12,
                                border: "1px solid #1f2937",
                                background: "rgba(17,21,34,0.55)",
                              }}
                            >
                              <div style={{ fontSize: 12, opacity: 0.75 }}>Participants</div>
                              <div style={{ fontSize: 18, fontWeight: 900, color: "#00f7d2" }}>{participants}</div>
                            </div>
                            <div
                              style={{
                                padding: "10px 10px",
                                borderRadius: 12,
                                border: "1px solid #1f2937",
                                background: "rgba(17,21,34,0.55)",
                              }}
                            >
                              <div style={{ fontSize: 12, opacity: 0.75 }}>Qty sold</div>
                              <div style={{ fontSize: 18, fontWeight: 900, color: "#e5e7eb" }}>{sold}</div>
                            </div>
                          </div>

                          <div
                            style={{
                              marginTop: 10,
                              height: 10,
                              borderRadius: 999,
                              background: "#111522",
                              border: "1px solid #1f2937",
                              overflow: "hidden",
                            }}
                          >
                            <div
                              style={{
                                height: "100%",
                                width: `${progressPercentage}%`,
                                background: "linear-gradient(90deg,#00f7d2,#5ce9ff)",
                                borderRadius: 999,
                                transition: "width .25s ease",
                              }}
                            />
                          </div>
                          <div style={{ marginTop: 6, fontSize: 12, opacity: 0.65 }}>
                            Share of total:{" "}
                            <strong style={{ color: "#5ce9ff" }}>{progressPercentage.toFixed(0)}%</strong>
                          </div>
                        </TierCard>
                      );
                    })}
                  </TierGridContainer>
                </div>
              </AuctionBox>
            ) : auctionTab === 'history' ? (
              <AuctionBox>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <h3 style={{ margin: 0, color: '#e5e7eb', fontSize: '18px' }}>Order History</h3>
                  <button
                    onClick={() => setShowMyOrders(!showMyOrders)}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '8px',
                      background: showMyOrders
                        ? 'linear-gradient(90deg, #00f7d2, #5ce9ff)'
                        : 'rgba(255, 255, 255, 0.05)',
                      border: showMyOrders ? 'none' : '1px solid #1f2937',
                      color: showMyOrders ? '#000' : '#e5e7eb',
                      fontWeight: showMyOrders ? 600 : 400,
                      cursor: 'pointer',
                      fontSize: '14px',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    {showMyOrders ? 'Show All Orders' : 'My Orders'}
                  </button>
                </div>
                {isLoadingBids ? (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
                    <Ring size={40} lineWeight={5} speed={2} color="#5ce9ff" />
                  </div>
                ) : (
                  <DataTable
                    columns={[
                      {
                        key: 'user_id',
                        label: 'User',
                        render: (value, row) => {
                          if (!value) return 'N/A';

                          const userInfo = row.user_info;
                          let userName = '';
                          let userImage = '';
                          let userEmail = '';

                          if (userInfo) {
                            if (Array.isArray(userInfo) && userInfo.length > 0) {
                              userName = userInfo[0].name || '';
                              userImage = userInfo[0].image || '';
                              userEmail = userInfo[0].email || '';
                            } else if (typeof userInfo === 'object') {
                              userName = userInfo.name || '';
                              userImage = userInfo.image || '';
                              userEmail = userInfo.email || '';
                            }
                          }

                          return (
                            <UserIdCell>
                              {userImage ? (
                                <UserAvatar src={userImage} alt={userName || 'User'} />
                              ) : (
                                <UserAvatarPlaceholder>
                                  {userName ? userName.charAt(0).toUpperCase() : 'U'}
                                </UserAvatarPlaceholder>
                              )}
                              <UserInfo>
                                <UserName>{userName || formatUserId(value)}</UserName>
                                {userEmail && <UserEmail>{userEmail}</UserEmail>}
                              </UserInfo>
                            </UserIdCell>
                          );
                        }
                      },
                      {
                        key: 'date',
                        label: 'Date',
                        render: (value, row) => {
                          return row.createdAt ? new Date(row.createdAt).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          }) : 'N/A';
                        }
                      },
                      {
                        key: 'quantity',
                        label: 'Quantity',
                        render: (value, row) => {
                          return row.quantity ? `${row.quantity} ${row.quantity > 1 ? 'units' : 'unit'}` : 'N/A';
                        }
                      },
                      {
                        key: 'symbol',
                        label: 'Symbol',
                        render: (value, row) => {
                          return productData?.symbol || 'USDT' || value || '-';
                        }
                      },
                      {
                        key: 'price',
                        label: 'Price',
                        render: (value, row) => {
                          return row.actual_price !== undefined ? `${row.actual_price} ` : 'N/A';
                        }
                      },
                      {
                        key: 'delivery_address',
                        label: 'Delivery Address'
                      },
                      {
                        key: 'status',
                        label: 'Status',
                        render: (value, row) => (
                          <StatusBadge status={value || 'pending'}>
                            {value || 'pending'}
                          </StatusBadge>
                        )
                      },
                      {
                        key: 'delivery_status',
                        label: 'Delivery Status',
                        render: (value, row) => {
                          const deliveryStatus = row.delivery_status || row.order_status || row.deliveryStatus || 'pending';
                          return (
                            <StatusBadge status={deliveryStatus}>
                              {deliveryStatus}
                            </StatusBadge>
                          );
                        }
                      }
                    ]}
                    data={(() => {
                      const allOrders = auctionData?.bidding_details || biddingDetails || [];
                      if (showMyOrders && currentUserId) {
                        return allOrders.filter(order => {
                          const orderUserId = order.user_id || order.userId || order.user?.user_id || order.user?._id || order.user?.id;
                          return orderUserId === currentUserId ||
                            String(orderUserId) === String(currentUserId);
                        });
                      }
                      return allOrders;
                    })()}
                    emptyMessage={showMyOrders ? "You haven't placed any orders yet for this auction." : "No orders have been placed yet for this auction."}
                    keyExtractor={(item) => item._id || item._id}
                  />
                )}
              </AuctionBox>
            ) : auctionTab === 'pastAuctions' ? (
              <AuctionBox>
                {productData?.past_auction && productData.past_auction.length > 0 ? (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px', tableLayout: 'fixed' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid #1f2937' }}>
                          <th style={{ padding: '12px', textAlign: 'left', color: '#9ca3af', fontWeight: 600, fontSize: '12px', textTransform: 'uppercase', verticalAlign: 'top' }}>Auction ID</th>
                          <th style={{ padding: '12px', textAlign: 'left', color: '#9ca3af', fontWeight: 600, fontSize: '12px', textTransform: 'uppercase', verticalAlign: 'top' }}>Start Date</th>
                          <th style={{ padding: '12px', textAlign: 'left', color: '#9ca3af', fontWeight: 600, fontSize: '12px', textTransform: 'uppercase', verticalAlign: 'top' }}>End Date</th>
                          <th style={{ padding: '12px', textAlign: 'left', color: '#9ca3af', fontWeight: 600, fontSize: '12px', textTransform: 'uppercase', verticalAlign: 'top' }}>Status</th>
                          <th style={{ padding: '12px', textAlign: 'left', color: '#9ca3af', fontWeight: 600, fontSize: '12px', textTransform: 'uppercase', verticalAlign: 'top' }}>Final Price</th>
                          <th style={{ padding: '12px', textAlign: 'left', color: '#9ca3af', fontWeight: 600, fontSize: '12px', textTransform: 'uppercase', verticalAlign: 'top' }}>Tiers</th>
                          <th style={{ padding: '12px', textAlign: 'center', color: '#9ca3af', fontWeight: 600, fontSize: '12px', textTransform: 'uppercase', verticalAlign: 'top' }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {productData.past_auction.map((auction, idx) => {
                          const startDate = auction.auction_start_time
                            ? new Date(auction.auction_start_time).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })
                            : 'N/A';
                          const endDate = auction.acution_end_time || auction.auction_end_time
                            ? new Date(auction.acution_end_time || auction.auction_end_time).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })
                            : 'N/A';
                          const tiersCount = auction.auction_tier?.length || 0;

                          return (
                            <tr key={auction._id} style={{ borderBottom: '1px solid #1f2937', textAlign: "start", transition: 'background 0.2s', verticalAlign: 'top' }}
                              onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)'}
                              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                              <td style={{ padding: '12px', color: '#e5e7eb', verticalAlign: 'top' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <span>{auction._id?.substring(0, 8) || 'N/A'}...</span>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const fullId = auction._id || '';
                                      navigator.clipboard?.writeText(fullId).then(() => {
                                        toast.success('Auction ID copied to clipboard');
                                      }).catch(() => {
                                        toast.error('Failed to copy');
                                      });
                                    }}
                                    style={{
                                      background: 'transparent',
                                      border: 'none',
                                      color: '#5ce9ff',
                                      cursor: 'pointer',
                                      padding: '4px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      borderRadius: '4px',
                                      transition: 'all 0.2s ease',
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.background = 'rgba(92, 233, 255, 0.1)';
                                      e.currentTarget.style.color = '#00f7d2';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.background = 'transparent';
                                      e.currentTarget.style.color = '#5ce9ff';
                                    }}
                                    title="Copy Auction ID"
                                  >
                                    <Copy size={14} />
                                  </button>
                                </div>
                              </td>
                              <td style={{ padding: '12px', color: '#e5e7eb', verticalAlign: 'top' }}>{startDate}</td>
                              <td style={{ padding: '12px', color: '#e5e7eb', verticalAlign: 'top' }}>{endDate}</td>
                              <td style={{ padding: '12px', verticalAlign: 'top' }}>
                                <StatusBadge status={auction.status || 'completed'}>
                                  {auction.status || 'completed'}
                                </StatusBadge>
                              </td>
                              <td style={{ padding: '12px', color: '#00f7d2', fontWeight: 600, verticalAlign: 'top' }}>
                                {auction.final_price > 0
                                  ? `${auction.final_price.toFixed(2)} ${productData?.symbol || 'USDT'}`
                                  : 'N/A'}
                              </td>
                              <td style={{ padding: '12px', color: '#9ca3af', verticalAlign: 'top' }}>
                                {tiersCount > 0 ? `${tiersCount} tier${tiersCount > 1 ? 's' : ''}` : 'N/A'}
                              </td>
                              <td style={{ padding: '12px', textAlign: 'center', verticalAlign: 'top' }}>
                                <button
                                  onClick={() => {
                                    setSelectedAuction(auction);
                                    const aggregateQty = auction?.aggregate_quantity && Array.isArray(auction.aggregate_quantity) && auction.aggregate_quantity.length > 0
                                      ? auction.aggregate_quantity[0]
                                      : null;
                                    console.log("🚀 ~ AuctionPRO ~ aggregateQty:", aggregateQty)
                                    setSelectedAuctionCal(aggregateQty);
                                    setShowDetailModal(true);
                                  }}
                                  style={{
                                    padding: '6px 12px',
                                    borderRadius: '6px',
                                    background: 'linear-gradient(90deg, #00f7d2, #5ce9ff)',
                                    color: '#000',
                                    border: 'none',
                                    cursor: 'pointer',
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    transition: 'opacity 0.2s'
                                  }}
                                  onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                                  onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                                >
                                  View Detail
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div style={{
                    textAlign: 'center',
                    padding: '3rem',
                    color: '#9ca3af',
                    fontSize: '14px'
                  }}>
                    No past auctions available for this product.
                  </div>
                )}
              </AuctionBox>
            ) : null}
          </>
        ) : null}
      </Section>

      <AuctionSidebar open={showAuctionSidebar}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24, flexShrink: 0 }}>
          <h3 style={{ margin: 0, color: "#e5e7eb" }}>{isEditingAuction ? "Edit Auction" : "Create Auction"}</h3>
          <button
            onClick={() => {
              setShowAuctionSidebar(false);
              setIsEditingAuction(false);
              setAuctionStartDateTime("");
              setAuctionEndDateTime("");
              setAuctionDescription("");
              setPricingTiers([]);
              setTierPrice("");
            }}
            style={{
              background: "transparent",
              border: "none",
              color: "#9ca3af",
              fontSize: 24,
              cursor: "pointer",
              padding: 0,
              width: 30,
              height: 30,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            ×
          </button>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 20, flex: 1, overflowY: "auto", overflowX: "hidden", minHeight: 0 }}>
          <FormGroup>
            <Label>Start Date & Time *</Label>
            <DateTimeInput
              type="datetime-local"
              value={auctionStartDateTime}
              onChange={(e) => setAuctionStartDateTime(e.target.value)}
              min={new Date().toISOString().slice(0, 16)}
            />
          </FormGroup>

          <FormGroup>
            <Label>End Date & Time *</Label>
            <DateTimeInput
              type="datetime-local"
              value={auctionEndDateTime}
              onChange={(e) => setAuctionEndDateTime(e.target.value)}
              min={auctionStartDateTime || new Date().toISOString().slice(0, 16)}
            />
          </FormGroup>

          <FormGroup>
            <Label>Description *</Label>
            <textarea
              value={auctionDescription}
              onChange={(e) => setAuctionDescription(e.target.value)}
              placeholder="Enter auction description"
              style={{
                width: "100%",
                padding: "12px",
                borderRadius: "8px",
                background: "#0d1220",
                border: "1px solid #1f2937",
                color: "#e5e7eb",
                fontSize: "14px",
                fontFamily: "inherit",
                resize: "vertical",
                minHeight: "100px",
                outline: "none",
              }}
            />
          </FormGroup>

          <FormGroup>
            <Label>Pricing Tiers *</Label>
            <TierInputGroup>
              <Input
                type="number"
                placeholder="Min Qty"
                value={(() => {
                  if (pricingTiers.length === 0) {
                    return 1; // First tier always starts at 1
                  }
                  // For subsequent tiers, min = previous tier max + 1
                  const previousTierMax = pricingTiers[pricingTiers.length - 1].maxQty;
                  return previousTierMax + 1;
                })()}
                disabled
                style={{
                  background: "#1f2937",
                  color: "#6b7280",
                  cursor: "not-allowed",
                }}
              />
              <Input
                type="number"
                placeholder="Max Qty"
                value={tierMaxQuantity}
                onChange={(e) => setTierMaxQuantity(e.target.value)}
                min={(() => {
                  if (pricingTiers.length === 0) return 1;
                  const previousTierMax = pricingTiers[pricingTiers.length - 1].maxQty;
                  return previousTierMax + 2; // Must be at least min + 1
                })()}
              />
              <Input
                type="number"
                placeholder="Price"
                value={tierPrice}
                onChange={(e) => setTierPrice(e.target.value)}
                min="0"
                step="0.01"
              />
              <AddTierButton 
                onClick={handleAddTier} 
                disabled={
                  !tierPrice || 
                  Number(tierPrice) <= 0 || 
                  !tierMaxQuantity || 
                  Number(tierMaxQuantity) <= 0
                }
              >
                Add
              </AddTierButton>
            </TierInputGroup>
            {pricingTiers.length > 0 && (
              <TierTable>
                <thead>
                  <tr>
                    <th>Tier</th>
                    <th>Min Qty</th>
                    <th>Max Qty</th>
                    <th>Price</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {pricingTiers.map((tier, index) => (
                    <tr key={index}>
                      <td>Tier {index + 1}</td>
                      <td>{tier.minQty}</td>
                      <td>
                        {editingTierIndex === index ? (
                          <input
                            type="number"
                            value={editTierMax}
                            onChange={(e) => setEditTierMax(e.target.value)}
                            min={tier.minQty + 1}
                            style={{
                              width: '80px',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              background: '#0d1220',
                              border: '1px solid #1f2937',
                              color: '#e5e7eb',
                              fontSize: '12px'
                            }}
                          />
                        ) : (
                          tier.maxQty
                        )}
                      </td>
                      <td>
                        {editingTierIndex === index ? (
                          <input
                            type="number"
                            value={editTierPrice}
                            onChange={(e) => setEditTierPrice(e.target.value)}
                            min="0"
                            step="0.01"
                            style={{
                              width: '80px',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              background: '#0d1220',
                              border: '1px solid #1f2937',
                              color: '#e5e7eb',
                              fontSize: '12px'
                            }}
                          />
                        ) : (
                          tier.price
                        )}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                          {editingTierIndex === index ? (
                            <>
                              <button
                                onClick={() => handleSaveTier(index)}
                                style={{
                                  background: '#22c55e',
                                  border: 'none',
                                  color: 'white',
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '11px',
                                  fontWeight: 500
                                }}
                              >
                                Save
                              </button>
                              <button
                                onClick={handleCancelEdit}
                                style={{
                                  background: '#6b7280',
                                  border: 'none',
                                  color: 'white',
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '11px',
                                  fontWeight: 500
                                }}
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => handleEditTier(index)}
                                style={{
                                  background: '#3b82f6',
                                  border: 'none',
                                  color: 'white',
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '11px',
                                  fontWeight: 500
                                }}
                              >
                                Edit
                              </button>
                              <button onClick={() => handleRemoveTier(index)}>Remove</button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </TierTable>
            )}
            {pricingTiers.length > 0 && (
              <div style={{ marginTop: 12, fontSize: 12, color: '#9ca3af', opacity: 0.7 }}>
                Next tier will be: {(() => {
                  const lastTierMax = pricingTiers[pricingTiers.length - 1].maxQty;
                  return `${lastTierMax + 1} - (enter max)`;
                })()}
              </div>
            )}
          </FormGroup>

          <div style={{ display: "flex", gap: 12, marginTop: 8, flexShrink: 0 }}>
            <button
              onClick={handleCreateAuction}
              disabled={isCreatingAuction}
              style={{
                flex: 1,
                padding: "12px 20px",
                borderRadius: 10,
                background: "linear-gradient(90deg, #00f7d2, #5ce9ff)",
                color: "#000",
                fontWeight: 700,
                cursor: isCreatingAuction ? "not-allowed" : "pointer",
                border: "none",
                fontSize: 14,
                opacity: isCreatingAuction ? 0.6 : 1,
              }}
            >
              {isCreatingAuction ? (isEditingAuction ? "Updating..." : "Creating...") : (isEditingAuction ? "Update Auction" : "Create Auction")}
            </button>
            <button
              onClick={() => {
                setShowAuctionSidebar(false);
                setIsEditingAuction(false);
                setAuctionStartDateTime("");
                setAuctionEndDateTime("");
                setAuctionDescription("");
                setPricingTiers([]);
                setTierPrice("");
              }}
              style={{
                padding: "12px 20px",
                borderRadius: 10,
                background: "#111827",
                border: "1px solid #1f2937",
                color: "#e5e7eb",
                cursor: "pointer",
                fontSize: 14,
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      </AuctionSidebar>

      {/* Auction Detail Modal */}
      {showDetailModal && selectedAuction && (
        <DetailModal onClick={(e) => e.target === e.currentTarget && setShowDetailModal(false)}>
          <DetailModalContent onClick={(e) => e.stopPropagation()}>
            <StyledCloseButton onClick={() => setShowDetailModal(false)}>×</StyledCloseButton>

            <h3 style={{ margin: '0 0 24px 0', color: '#e5e7eb', fontSize: '20px' }}>
              Auction Details
            </h3>

            {(activeAuction?.description || selectedAuction?.description) && (
              <DetailSection style={{ marginBottom: 20 }}>
                <DetailLabel>Description</DetailLabel>
                <DetailValue style={{ 
                  lineHeight: '1.6',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word'
                }}>
                  {activeAuction?.description || selectedAuction?.description}
                </DetailValue>
              </DetailSection>
            )}

            <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
              <DetailSection style={{ flex: 1 }}>
                <DetailLabel>Start Date</DetailLabel>
                <DetailValue>
                  {selectedAuction.auction_start_time
                    ? new Date(selectedAuction.auction_start_time).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit'
                    })
                    : 'N/A'}
                </DetailValue>
              </DetailSection>
              <DetailSection style={{ flex: 1 }}>
                <DetailLabel>End Date</DetailLabel>
                <DetailValue>
                  {(selectedAuction.acution_end_time || selectedAuction.auction_end_time)
                    ? new Date(selectedAuction.acution_end_time || selectedAuction.auction_end_time).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit'
                    })
                    : 'N/A'}
                </DetailValue>
              </DetailSection>
            </div>

            <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
              <DetailSection style={{ flex: 1 }}>
                <DetailLabel>Aggregated Quantity</DetailLabel>
                <DetailValue bold>{selectedAuctionCal?.total_quantity || 0}</DetailValue>
              </DetailSection>
              <DetailSection style={{ flex: 1 }}>
                <DetailLabel>Total Participants</DetailLabel>
                <DetailValue bold>{selectedAuction?.total_paticipent || 0}</DetailValue>
              </DetailSection>
            </div>

            <DetailSection>
              <DetailLabel>Active Tier Price </DetailLabel>
              <DetailValue highlight bold>
                {(() => {
                  const tiers = selectedAuction.auction_tier || [];
                  if (tiers.length === 0) return 'N/A';
                  const firstTier = tiers[0];
                  return `${firstTier.price?.toFixed(6) || '0.000000'} ${productData?.symbol || 'USDT'}${productData?.network_name ? ` (${productData.network_name})` : ''}`;
                })()}
              </DetailValue>
            </DetailSection>

            <div style={{
              height: '1px',
              background: '#1f2937',
              margin: '24px 0',
              width: '100%'
            }} />

            <div style={{
              height: '1px',
              background: '#1f2937',
              margin: '24px 0',
              width: '100%'
            }} />

            <DetailSection>
              <DetailLabel style={{ marginBottom: '12px' }}>Tier Pricing</DetailLabel>
              {selectedAuction.auction_tier && selectedAuction.auction_tier.length > 0 ? (
                selectedAuction.auction_tier.map((tier, index) => {
                  const tierSummaryData = tierSummary?.find(ts => ts.tier_id === tier._id);
                  const isActive = activeTierId && tier._id === activeTierId;
                  // Find the index of the active tier
                  const activeTierIndex = activeTierId
                    ? selectedAuction.auction_tier.findIndex(t => t._id === activeTierId)
                    : -1;
                  // Check if this tier is at or above the active tier
                  const isAboveActive = activeTierIndex >= 0 && index <= activeTierIndex;
                  return (
                    <TierBox key={index} active={isActive} isAboveActive={isAboveActive && !isActive}>
                      {isActive && <PulseDot />}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                        {(() => {
                          const tierMin = tier.minQty || tier.min || 0;
                          const tierMax = tier.maxQty || null;
                          const qtyRange = tierMax ? `${tierMin}-${tierMax}` : `≥${tierMin}`;
                          return (
                            <span><strong>Tier {index + 1}:</strong> {qtyRange} units → {tier.price?.toFixed(6) || '0.000000'} {productData?.symbol || 'USDT'}</span>
                          );
                        })()}
                        {tierSummaryData && (
                          <div style={{ fontSize: '12px', opacity: 0.7, display: 'flex', gap: '12px' }}>
                            <span>Qty: {tierSummaryData.total_quantity || 0}</span>
                            <span>Participants: {tierSummaryData.total_participants || 0}</span>
                          </div>
                        )}
                      </div>
                    </TierBox>
                  );
                })
              ) : (
                <DetailValue>No tiers available</DetailValue>
              )}
            </DetailSection>
          </DetailModalContent>
        </DetailModal>
      )}

      <PaymentSidebar
        open={showBidSidebar}
        onOpenChange={setShowBidSidebar}
        mode="bid"
        title="Place Bid"
        subtitle="Submit your bid"
        productSymbol={productData?.symbol}
        orderDetail={bidPaymentProps.orderDetail}
        products={bidPaymentProps.products}
        shippingCost={deliveryCharges}
        categoryId={productData?.category_id}
        bidQuantity={bidQuantity}
        onBidQuantityChange={(q) => {
          const availableQty = maxTierQty > 0 ? maxTierQty - orders : Infinity;
          if (q == null || q === '') setBidQuantity(q);
          else if (q >= 1 && q <= availableQty) setBidQuantity(q);
          else if (q > availableQty) {
            toast.error(`Maximum quantity allowed is ${availableQty}`);
            setBidQuantity(availableQty);
          }
        }}
        bidTotalAmount={bidPaymentProps.bidTotalAmount}
        onBidPay={async (deliveryInfo) => {
          console.log('[PlaceBid] onBidPay called', { deliveryInfo });
          const deliveryOverride = {
            deliveryAddress: deliveryInfo.deliveryAddress,
            deliveryCountry: deliveryInfo.deliveryCountry,
            secondDeliveryAddress: deliveryInfo.secondDeliveryAddress || '',
            phoneNumber: deliveryInfo.phoneNumber,
          };
          // Attach listener BEFORE emit so we never miss a fast server response
          return new Promise((resolve, reject) => {
            const handleResponse = (res) => {
              console.log('[PlaceBid] add-auction-bid-response received', { res, hasMessage: !!res?.message, message: res?.message });
              socket.off('add-auction-bid-response', handleResponse);
              isSubmittingBidRef.current = false;
              setIsSubmittingBid(false);
              if (res?.message && (res.message === 'Insufficient balance' || String(res.message).toLowerCase().includes('insufficient'))) {
                console.log('[PlaceBid] response: insufficient balance, rejecting');
                reject(new Error(res.message || 'Insufficient balance'));
                return;
              }
              if (res?.message) {
                console.log('[PlaceBid] response: success, resolving');
                if (onProductDataUpdate) onProductDataUpdate();
                resolve();
                return;
              }
              console.log('[PlaceBid] response: no message, resolving anyway');
              if (onProductDataUpdate) onProductDataUpdate();
              resolve();
            };
            console.log('[PlaceBid] attaching once listener for add-auction-bid-response (before emit)');
            socket.once('add-auction-bid-response', handleResponse);

            const didEmit = handlePlaceBid(deliveryOverride);
            console.log('[PlaceBid] handlePlaceBid returned didEmit=', didEmit);
            if (!didEmit) {
              socket.off('add-auction-bid-response', handleResponse);
              reject(new Error('Validation failed'));
            }
          });
        }}
        onPurchaseSuccess={onProductDataUpdate}
      />

      <ProductEditSidebar open={showProductEditSidebar}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24, flexShrink: 0 }}>
          <h3 style={{ margin: 0, color: "#e5e7eb" }}>Edit Product</h3>
          <button
            onClick={() => {
              setShowProductEditSidebar(false);
              setProductEditForm({
                title: "",
                brand: "",
                description: "",
                warrantyInMonths: 0,
                deliveryCharges: 0,
                pickupAddress: "",
              });
            }}
            style={{
              background: "transparent",
              border: "none",
              color: "#9ca3af",
              fontSize: 24,
              cursor: "pointer",
              padding: 0,
              width: 30,
              height: 30,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            ×
          </button>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 20, flex: 1, overflowY: "auto", overflowX: "hidden", minHeight: 0 }}>
          <FormGroup>
            <Label>Product Title *</Label>
            <Input
              type="text"
              value={productEditForm.title}
              onChange={(e) => setProductEditForm({ ...productEditForm, title: e.target.value })}
              placeholder="Enter product title"
            />
          </FormGroup>

          <FormGroup>
            <Label>Brand</Label>
            <Input
              type="text"
              value={productEditForm.brand}
              onChange={(e) => setProductEditForm({ ...productEditForm, brand: e.target.value })}
              placeholder="Enter product brand"
            />
          </FormGroup>

          <FormGroup>
            <Label>Description</Label>
            <textarea
              value={productEditForm.description}
              onChange={(e) => setProductEditForm({ ...productEditForm, description: e.target.value })}
              placeholder="Enter product description"
              style={{
                width: "100%",
                padding: "12px",
                borderRadius: "8px",
                background: "#0d1220",
                border: "1px solid #1f2937",
                color: "#e5e7eb",
                fontSize: "14px",
                fontFamily: "inherit",
                resize: "vertical",
                minHeight: "100px",
                outline: "none",
              }}
            />
          </FormGroup>

          <FormGroup>
            <Label>Warranty (Months)</Label>
            <Input
              type="number"
              value={productEditForm.warrantyInMonths}
              onChange={(e) => setProductEditForm({ ...productEditForm, warrantyInMonths: parseInt(e.target.value) || 0 })}
              placeholder="Enter warranty in months"
              min="0"
            />
          </FormGroup>

          <FormGroup>
            <Label>Delivery Charges</Label>
            <Input
              type="number"
              value={productEditForm.deliveryCharges}
              onChange={(e) => setProductEditForm({ ...productEditForm, deliveryCharges: parseFloat(e.target.value) || 0 })}
              placeholder="Enter delivery charges"
              min="0"
              step="0.01"
            />
          </FormGroup>

          <FormGroup>
            <Label>Pickup Address</Label>
            <textarea
              value={productEditForm.pickupAddress}
              onChange={(e) => setProductEditForm({ ...productEditForm, pickupAddress: e.target.value })}
              placeholder="Enter pickup address"
              style={{
                width: "100%",
                padding: "12px",
                borderRadius: "8px",
                background: "#0d1220",
                border: "1px solid #1f2937",
                color: "#e5e7eb",
                fontSize: "14px",
                fontFamily: "inherit",
                resize: "vertical",
                minHeight: "80px",
                outline: "none",
              }}
            />
          </FormGroup>
        </div>

        <div style={{ display: "flex", gap: 12, marginTop: "auto", paddingTop: 20, flexShrink: 0 }}>
          <button
            onClick={handleUpdateProduct}
            disabled={isUpdatingProduct}
            style={{
              flex: 1,
              padding: "12px 20px",
              borderRadius: 10,
              background: "linear-gradient(90deg, #00f7d2, #5ce9ff)",
              color: "#000",
              fontWeight: 700,
              cursor: isUpdatingProduct ? "not-allowed" : "pointer",
              border: "none",
              fontSize: 14,
              opacity: isUpdatingProduct ? 0.6 : 1,
            }}
          >
            {isUpdatingProduct ? "Updating..." : "Update Product"}
          </button>
          <button
            onClick={() => {
              setShowProductEditSidebar(false);
              setProductEditForm({
                title: "",
                brand: "",
                description: "",
                warrantyInMonths: 0,
                deliveryCharges: 0,
                pickupAddress: "",
              });
            }}
            style={{
              padding: "12px 20px",
              borderRadius: 10,
              background: "rgba(255, 255, 255, 0.1)",
              color: "#e5e7eb",
              fontWeight: 600,
              cursor: "pointer",
              border: "1px solid #1f2937",
              fontSize: 14,
            }}
          >
            Cancel
          </button>
        </div>
      </ProductEditSidebar>

      {showConfirmModal && confirmModalConfig && typeof document !== 'undefined' && ReactDOM.createPortal(
        <ModalOverlay onClick={confirmModalConfig.onCancel}>
          <ModalContainer onClick={(e) => e.stopPropagation()}>
            <ModalTitle>
              <AlertTriangle size={20} color="#ef4444" />
              {confirmModalConfig.title}
            </ModalTitle>
            <ModalMessage>{confirmModalConfig.message}</ModalMessage>
            <ModalActions>
              <ModalButton onClick={confirmModalConfig.onCancel}>
                Cancel
              </ModalButton>
              <ModalButton variant="danger" onClick={confirmModalConfig.onConfirm}>
                Confirm
              </ModalButton>
            </ModalActions>
          </ModalContainer>
        </ModalOverlay>,
        document.body
      )}
    </>
  );
}

export default AuctionPRO;

